import queryString from 'query-string'
import ApisServiceApp from './ApisServiceApp'
import { apiRequest } from '@/utils/apiRequestUtils'
import { normalizeDetailResponse, normalizeListResponse, type PagedListResult } from '@/utils/apiResponseUtils'
import { postMultipartFile } from '@/utils/blobApiUtils'
import type {
  IThuongHieu,
  IThuongHieuImportRow,
  IThuongHieuPayload,
  TrangThaiThuongHieu,
} from '@/interfaces/thuongHieuInterfaces'

const PREFIX = '/thuong-hieu'

function mapThuongHieuFromApi(raw: Record<string, unknown>): IThuongHieu {
  const trangThai = raw.trangThai
  const rawNguoiTao = raw.nguoiTao as { id?: number; username?: string } | null | undefined
  const rawNguoiCapNhat = raw.nguoiCapNhat as { id?: number; username?: string } | null | undefined
  const rawCount = raw._count as { hangHoas?: number } | null | undefined
  return {
    id: String(raw.id ?? ''),
    maThuongHieu: String(raw.maThuongHieu ?? ''),
    tenThuongHieu: String(raw.tenThuongHieu ?? ''),
    ghiChu: raw.ghiChu != null ? String(raw.ghiChu) : null,
    trangThai:
      trangThai === 'dangHoatDong' || trangThai === 'ngungHoatDong' ? trangThai : 'dangHoatDong',
    ngayTao: raw.ngayTao != null ? String(raw.ngayTao) : null,
    ngayCapNhat: raw.ngayCapNhat != null ? String(raw.ngayCapNhat) : null,
    nguoiTao: rawNguoiTao?.username ? { id: Number(rawNguoiTao.id ?? 0), username: rawNguoiTao.username } : null,
    nguoiCapNhat: rawNguoiCapNhat?.username
      ? { id: Number(rawNguoiCapNhat.id ?? 0), username: rawNguoiCapNhat.username }
      : null,
    soHangHoa: typeof rawCount?.hangHoas === 'number' ? rawCount.hangHoas : 0,
    logoUrl: raw.logoUrl != null ? String(raw.logoUrl) : null,
  }
}

function toPayload(body: IThuongHieuPayload) {
  const payload: Record<string, unknown> = { tenThuongHieu: body.tenThuongHieu.trim() }
  if (body.maThuongHieu?.trim()) payload.maThuongHieu = body.maThuongHieu.trim()
  if (body.ghiChu?.trim()) payload.ghiChu = body.ghiChu.trim()
  if (body.logoUrl != null) payload.logoUrl = body.logoUrl.trim() ? body.logoUrl.trim() : null
  return payload
}

export function thuongHieuListQueryFromSearch(search: string): Record<string, string | number> {
  const q = queryString.parse(search.replace(/^\?/, ''))
  const out: Record<string, string | number> = {}
  const page = Number(q.page)
  const pageSize = Number(q.pageSize)
  if (Number.isFinite(page) && page > 0) out.page = page
  if (Number.isFinite(pageSize) && pageSize > 0) out.pageSize = pageSize
  const searchVal = q.search
  if (typeof searchVal === 'string' && searchVal.trim()) out.search = searchVal.trim()
  const maThuongHieu = q.maThuongHieu
  if (typeof maThuongHieu === 'string' && maThuongHieu.trim()) out.maThuongHieu = maThuongHieu.trim()
  const tenThuongHieu = q.tenThuongHieu
  if (typeof tenThuongHieu === 'string' && tenThuongHieu.trim()) out.tenThuongHieu = tenThuongHieu.trim()
  const trangThai = q.trangThai
  if (trangThai === 'dangHoatDong' || trangThai === 'ngungHoatDong') out.trangThai = trangThai
  if (typeof q.sort === 'string' && q.sort.trim()) out.sort = q.sort.trim()
  if (q.sortDir === 'asc' || q.sortDir === 'desc') out.sortDir = q.sortDir
  return out
}

export function fetchDanhSachThuongHieu(search: string): Promise<PagedListResult<IThuongHieu>> {
  const params = thuongHieuListQueryFromSearch(search)
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/danh-sach-thuong-hieu`, { params }).then((res: unknown) => {
      const { data, paging } = normalizeListResponse(res as Parameters<typeof normalizeListResponse>[0])
      return {
        data: (data as Record<string, unknown>[]).map(mapThuongHieuFromApi),
        paging,
      }
    }),
  )
}

export function fetchChiTietThuongHieu(id: string | number): Promise<IThuongHieu> {
  return apiRequest(
    ApisServiceApp.get(`${PREFIX}/${id}/chi-tiet-thuong-hieu`).then((res: unknown) =>
      mapThuongHieuFromApi(normalizeDetailResponse(res) as Record<string, unknown>),
    ),
  )
}

export function taoThuongHieu(
  body: IThuongHieuPayload,
): Promise<{ data: IThuongHieu }> {
  return apiRequest(
    ApisServiceApp.post(`${PREFIX}/tao-thuong-hieu`, toPayload(body)),
  ).then((res: unknown) => {
    // Backend trả `{ statusCode, message, data: IThuongHieu }` qua TransformInterceptor.
    // Tự động unwrap để caller nhận `entity` trực tiếp.
    const wrapped = res as { data?: Record<string, unknown> } | Record<string, unknown>
    const raw = (wrapped as { data?: Record<string, unknown> }).data ?? (wrapped as Record<string, unknown>)
    return { data: mapThuongHieuFromApi(raw) }
  })
}

export function capNhatThuongHieu(id: string | number, body: IThuongHieuPayload) {
  return apiRequest(ApisServiceApp.put(`${PREFIX}/${id}/cap-nhat-thuong-hieu`, toPayload(body)))
}

export function xoaThuongHieu(id: string | number) {
  return apiRequest(ApisServiceApp.delete(`${PREFIX}/${id}/xoa-thuong-hieu`))
}

export type XoaNhieuThuongHieuResult = {
  ok: number
  failed: Array<{ id: string; message: string }>
}

/** Xóa nhiều thương hiệu — gọi API xóa từng id (loại trùng id). */
export async function xoaNhieuThuongHieu(ids: string[]): Promise<XoaNhieuThuongHieuResult> {
  const uniqueIds = Array.from(new Set(ids.map((id) => String(id).trim()).filter(Boolean)))
  const failed: XoaNhieuThuongHieuResult['failed'] = []
  let ok = 0
  for (const id of uniqueIds) {
    try {
      await xoaThuongHieu(id)
      ok += 1
    } catch (e) {
      failed.push({
        id,
        message: (e as { message?: string })?.message ?? '',
      })
    }
  }
  return { ok, failed }
}

export function chuyenTrangThaiThuongHieu(id: string | number, trangThai: TrangThaiThuongHieu) {
  return apiRequest(ApisServiceApp.patch(`${PREFIX}/${id}/chuyen-trang-thai-thuong-hieu`, { trangThai }))
}

export function nhapDanhSachThuongHieu(rows: IThuongHieuImportRow[]) {
  return apiRequest(ApisServiceApp.post(`${PREFIX}/thuong-hieu-nhap-danh-sach`, rows))
}

export function nhapThuongHieuTuExcel(file: File) {
  return apiRequest(postMultipartFile(`${PREFIX}/nhap-thuong-hieu-tu-excel`, file))
}
